import { useFlowScheduleRuns } from "@/api/flowSchedules";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import { formatDayTime } from "@/panes/automation/cadence";
import { route } from "@/utils/route";
import { type FlowScheduleRun } from "@cohesive/api-client";
import { CircleCheck, Play } from "lucide-react";

/**
 * Recent runs — each run is a flow session, so a click opens its transcript
 * in SessionPane (the pane back-stack returns here). Status is coarse by
 * design: the backend has no failed state, so runs read running | completed.
 */
export function AutomationRunsList({
  flowScheduleId,
}: {
  flowScheduleId: string;
}) {
  const appNavigate = useAppNavigate();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFlowScheduleRuns(flowScheduleId);

  const runs: FlowScheduleRun[] =
    data?.pages.flatMap((page) => page.data.runs) ?? [];

  const openRun = (run: FlowScheduleRun) => {
    appNavigate({
      pane: "rightPane",
      to: route(ROUTES.SESSIONS.SESSION, { sessionId: run.id }),
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1">
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-7 w-2/3" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-n-40">Couldn&apos;t load runs.</p>;
  }

  if (runs.length === 0) {
    return <p className="text-sm text-n-40">No runs yet.</p>;
  }

  return (
    <div className="flex flex-col">
      {runs.map((run) => {
        const isRunning = run.status === "running";
        return (
          <button
            key={run.id}
            type="button"
            onClick={() => openRun(run)}
            className="flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 text-left transition-colors hover:bg-n-05"
          >
            {isRunning ? (
              <Play className="size-3.5 shrink-0 text-lc-progress" />
            ) : (
              <CircleCheck className="size-3.5 shrink-0 text-lc-active" />
            )}
            {/* One automation's runs differ by time, not name — the start
                time is the label; status only speaks up while in flight. */}
            <span className="min-w-0 flex-1 truncate text-sm text-n-80">
              {formatDayTime(run.created_at)}
            </span>
            {isRunning && (
              <span className="shrink-0 text-xs text-lc-progress">Running</span>
            )}
          </button>
        );
      })}
      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="flex min-h-7 w-full items-center rounded-md px-2 text-left text-xs text-n-50 transition-colors hover:bg-n-05 hover:text-n-70 disabled:opacity-60"
        >
          {isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}
    </div>
  );
}
